import { SendIcon } from 'lucide-react-native';
import * as React from 'react';
import { View } from 'react-native';

import { Button } from '@sixam/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@sixam/ui/card';
import { Text } from '@sixam/ui/text';
import { sendAdminEventUpdateNotification, type EventNotificationLog } from '@/lib/notifications';

type AdminEventNotificationPanelProps = Readonly<{
  eventId: string;
  flat?: boolean;
  initialLog?: EventNotificationLog | null;
  onSent?: () => void;
  tenantSlug: string;
}>;

export function AdminEventNotificationPanel({
  eventId,
  flat = false,
  initialLog = null,
  onSent,
  tenantSlug,
}: AdminEventNotificationPanelProps) {
  const [log, setLog] = React.useState<EventNotificationLog | null>(initialLog);
  const [isSending, setIsSending] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  async function sendUpdate() {
    setIsSending(true);
    setError(null);
    try {
      const nextLog = await sendAdminEventUpdateNotification(tenantSlug, eventId);
      setLog(nextLog);
      onSent?.();
    } catch (sendError) {
      setError(sendError instanceof Error ? sendError.message : 'Could not send notification.');
    } finally {
      setIsSending(false);
    }
  }

  return (
    <AdminEventNotificationSummary
      error={error}
      flat={flat}
      isSending={isSending}
      log={log}
      onSend={sendUpdate}
    />
  );
}

export function AdminEventNotificationSummary({
  error,
  flat = false,
  isSending,
  log,
  onSend,
}: Readonly<{
  error?: string | null;
  flat?: boolean;
  isSending?: boolean;
  log?: EventNotificationLog | null;
  onSend?: () => void;
}>) {
  const content = (
    <View className="gap-4">
      <Text variant="muted">
        Send a push update to registered members with active Expo push tokens.
      </Text>
      <Button disabled={isSending} onPress={onSend}>
        <SendIcon color="white" size={18} />
        <Text>{isSending ? 'Sending…' : 'Send update'}</Text>
      </Button>
      {log ? (
        <View className="flex-row flex-wrap gap-3">
          <Metric label="Audience" value={log.audience_count} />
          <Metric label="Sent" value={log.sent_count} />
          <Metric label="Failed" value={log.failed_count} />
          <Metric label="Skipped" value={log.skipped_count} />
        </View>
      ) : (
        <Text variant="mono">No event update sent yet.</Text>
      )}
      {log?.last_error ? <Text className="text-sm text-destructive">{log.last_error}</Text> : null}
      {error ? <Text className="text-sm font-medium text-destructive">{error}</Text> : null}
    </View>
  );

  if (flat) {
    return content;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Event notification</CardTitle>
      </CardHeader>
      <CardContent>{content}</CardContent>
    </Card>
  );
}

function Metric({ label, value }: Readonly<{ label: string; value: number }>) {
  return (
    <View className="min-w-20 gap-1">
      <Text variant="label" className="text-muted-foreground">
        {label}
      </Text>
      <Text variant="mono">{value}</Text>
    </View>
  );
}
